import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Chip,
  Pagination,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { ensureUiScope } from '../services/api';
import { auditApi, AuditLogEntry } from '../services/auditApi';
import { camerasApi } from '../services/camerasApi';
import { policiesApi } from '../services/policiesApi';

type ReviewSessionEvent = {
  id: string;
  timestamp: string;
  stepTitle: string;
  phaseTitle: string;
  action: 'completed' | 'reopened';
};

type AuditDisplayRow = AuditLogEntry & {
  displayId: string;
  displayAction?: string;
  groupedReviewSession?: {
    reviewSessionId: string;
    events: ReviewSessionEvent[];
  };
};

function fmtTs(ts?: string | null): string {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return String(ts);
  }
}

function normalizeResourceIdFromUi(
  resourceType: string,
  raw: string,
  options: Array<{ id: string; label: string }>
): string {
  const v = String(raw || '').trim();
  if (!v) return '';

  // Only normalize for the autocomplete-backed types (camera/policy).
  if (resourceType !== 'camera' && resourceType !== 'policy') return v;

  // Exact match by id.
  const byId = options.find((o) => o.id === v);
  if (byId) return byId.id;

  // Exact match by label.
  const byLabel = options.find((o) => o.label === v);
  if (byLabel) return byLabel.id;

  // Our labels are formatted like: "Name (id)" — extract the id.
  const m = v.match(/\(([^)]+)\)\s*$/);
  if (m && m[1]) return String(m[1]).trim();

  // User pasted an id (or something else) — pass through as exact-match query.
  return v;
}

function isReviewSessionAuditRow(row: AuditLogEntry): boolean {
  const details = row?.details && typeof row.details === 'object' ? row.details : {};
  const action = String((details as any)?.action || '').trim().toLowerCase();
  return (
    String(row?.resource_type || '').trim().toLowerCase() === 'incident'
    && Boolean(String((details as any)?.review_session_id || '').trim())
    && (action === 'review_journey_step_completed' || action === 'review_journey_step_reopened')
  );
}

function buildAuditDisplayRows(rows: AuditLogEntry[]): AuditDisplayRow[] {
  const grouped = new Map<string, AuditDisplayRow>();
  const ordered: AuditDisplayRow[] = [];

  for (const row of rows || []) {
    if (!isReviewSessionAuditRow(row)) {
      ordered.push({ ...row, displayId: String(row.id) });
      continue;
    }
    const details = (row.details && typeof row.details === 'object' ? row.details : {}) as any;
    const sessionId = String(details.review_session_id || '').trim();
    const groupKey = `${String(row.resource_type || '')}:${String(row.resource_id || '')}:${sessionId}`;
    const event: ReviewSessionEvent = {
      id: String(row.id),
      timestamp: String(row.timestamp || ''),
      stepTitle: String(details.step_title || details.step_id || 'Review step'),
      phaseTitle: String(details.phase_title || ''),
      action: String(details.action || '').trim().toLowerCase() === 'review_journey_step_reopened' ? 'reopened' : 'completed',
    };

    if (!grouped.has(groupKey)) {
      const nextRow: AuditDisplayRow = {
        ...row,
        displayId: `review-session:${groupKey}`,
        displayAction: 'REVIEW_SESSION',
        groupedReviewSession: {
          reviewSessionId: sessionId,
          events: [event],
        },
      };
      grouped.set(groupKey, nextRow);
      ordered.push(nextRow);
      continue;
    }

    const existing = grouped.get(groupKey)!;
    existing.groupedReviewSession = {
      reviewSessionId: sessionId,
      events: [...(existing.groupedReviewSession?.events || []), event],
    };
    const existingTs = Date.parse(String(existing.timestamp || '')) || 0;
    const nextTs = Date.parse(String(row.timestamp || '')) || 0;
    if (nextTs > existingTs) {
      existing.timestamp = row.timestamp;
      existing.username = row.username;
      existing.user_id = row.user_id;
      existing.success = row.success;
      existing.error_message = row.error_message;
      existing.details = row.details;
      existing.old_value = row.old_value;
      existing.new_value = row.new_value;
    }
  }

  return ordered.sort((left, right) => {
    const leftTs = Date.parse(String(left.timestamp || '')) || 0;
    const rightTs = Date.parse(String(right.timestamp || '')) || 0;
    return rightTs - leftTs;
  });
}

function summarizeAuditDetail(row: AuditDisplayRow): string {
  if (row.groupedReviewSession) {
    const events = [...(row.groupedReviewSession.events || [])].sort((left, right) => {
      const leftTs = Date.parse(left.timestamp) || 0;
      const rightTs = Date.parse(right.timestamp) || 0;
      return leftTs - rightTs;
    });
    return events
      .map((event) => `${event.stepTitle} (${event.action}${event.phaseTitle ? ` • ${event.phaseTitle}` : ''})`)
      .join(' | ');
  }
  if (row.error_message) return `error: ${row.error_message}`;
  return row.details ? JSON.stringify(row.details).slice(0, 240) : '';
}

export default function AuditLogPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const syncingFromUrlRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [action, setAction] = useState<string>('');
  const [resourceType, setResourceType] = useState<string>('');
  const [resourceId, setResourceId] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [onlyFailures, setOnlyFailures] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage] = useState(50);

  const [rows, setRows] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  // Resource suggestions (F-18 UX): make Resource ID easier to select
  const [resourceOptions, setResourceOptions] = useState<Array<{ id: string; label: string }>>([]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const scope = ensureUiScope();
        const scopedSiteId = String(scope.siteId || '').trim();
        if (!scopedSiteId) {
          if (mounted) setResourceOptions([]);
          return;
        }
        // Only provide suggestions for high-frequency resource types; fall back to free text otherwise.
        if (resourceType === 'camera') {
          const res = await camerasApi.listCameras(scopedSiteId);
          const items = Array.isArray(res?.items) ? res.items : [];
          const opts = items
            .filter((c: any) => c?.camera_id)
            .map((c: any) => ({
              id: String(c.camera_id),
              label: `${c.name || c.display_name || c.camera_id} (${c.camera_id})`,
            }));
          if (mounted) setResourceOptions(opts);
        } else if (resourceType === 'policy') {
          const res = await policiesApi.listPolicies(scopedSiteId, true);
          const items = Array.isArray(res?.policies) ? res.policies : [];
          const opts = items
            .filter((p: any) => p?.policy_id)
            .map((p: any) => ({
              id: String(p.policy_id),
              label: `${p.name || p.policy_id} (${p.policy_type || 'policy'})`,
            }));
          if (mounted) setResourceOptions(opts);
        } else {
          if (mounted) setResourceOptions([]);
        }
      } catch {
        if (mounted) setResourceOptions([]);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [resourceType]);

  const resourceIdQuery = useMemo(() => {
    return normalizeResourceIdFromUi(resourceType, resourceId, resourceOptions);
  }, [resourceType, resourceId, resourceOptions]);

  // F-18: Support deep links into Audit Log (example: /audit?resource_type=camera&resource_id=cam_xxx)
  useEffect(() => {
    try {
      syncingFromUrlRef.current = true;
      const qp = new URLSearchParams(location.search || '');
      const qpAction = (qp.get('action') || '').trim();
      const qpResourceType = (qp.get('resource_type') || '').trim();
      const qpResourceId = (qp.get('resource_id') || '').trim();
      const qpUsername = (qp.get('username') || '').trim();
      const qpSuccess = (qp.get('success') || '').trim().toLowerCase();
      const qpPage = parseInt((qp.get('page') || '1').trim(), 10);

      setAction(qpAction);
      setResourceType(qpResourceType);
      setResourceId(qpResourceId);
      setUsername(qpUsername);
      setOnlyFailures(qpSuccess === 'false');
      setPage(Number.isFinite(qpPage) && qpPage > 0 ? qpPage : 1);
    } catch {
      // ignore invalid query params
    } finally {
      // allow state->url sync on next tick
      setTimeout(() => {
        syncingFromUrlRef.current = false;
      }, 0);
    }
  }, [location.search]);

  // Keep URL in sync with filter state (fixes "Clear shows unfiltered data but URL still has filters")
  useEffect(() => {
    if (syncingFromUrlRef.current) return;

    const qp = new URLSearchParams();
    if (action) qp.set('action', action);
    if (resourceType) qp.set('resource_type', resourceType);
    if (resourceIdQuery) qp.set('resource_id', resourceIdQuery);
    if (username) qp.set('username', username);
    if (onlyFailures) qp.set('success', 'false');
    if (page && page !== 1) qp.set('page', String(page));

    const nextSearch = qp.toString() ? `?${qp.toString()}` : '';
    const currSearch = location.search || '';
    if (nextSearch !== currSearch) {
      navigate(`${location.pathname}${nextSearch}`, { replace: true });
    }
  }, [action, resourceType, resourceIdQuery, username, onlyFailures, page, location.pathname, location.search, navigate]);

  const params = useMemo(() => {
    return {
      action: action || undefined,
      resource_type: resourceType || undefined,
      resource_id: resourceIdQuery || undefined,
      username: username || undefined,
      success: onlyFailures ? 'false' : undefined,
      page,
      per_page: perPage,
    } as const;
  }, [action, resourceType, resourceIdQuery, username, onlyFailures, page, perPage]);
  const displayRows = useMemo(() => buildAuditDisplayRows(rows), [rows]);

  const openFiltered = (next: { action?: string; resource_type?: string; resource_id?: string }) => {
    const qp = new URLSearchParams();
    if (next.action) qp.set('action', next.action);
    if (next.resource_type) qp.set('resource_type', next.resource_type);
    if (next.resource_id) qp.set('resource_id', next.resource_id);
    if (username) qp.set('username', username);
    if (onlyFailures) qp.set('success', 'false');
    qp.set('page', '1');
    navigate(`/audit?${qp.toString()}`);
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await auditApi.list(params);
        if (!mounted) return;
        setRows(res.logs || []);
        setTotal(res.total || 0);
        setPages(res.pages || 1);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.response?.data?.error || e?.message || 'Failed to load audit logs');
        setRows([]);
        setTotal(0);
        setPages(1);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [params]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Audit Log
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Action"
              value={action}
              onChange={(e) => {
                setPage(1);
                setAction(e.target.value);
              }}
              select
              size="small"
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="CREATE">CREATE</MenuItem>
              <MenuItem value="UPDATE">UPDATE</MenuItem>
              <MenuItem value="DELETE">DELETE</MenuItem>
              <MenuItem value="DEPLOY">DEPLOY</MenuItem>
              <MenuItem value="ROLLBACK">ROLLBACK</MenuItem>
              <MenuItem value="LOGIN">LOGIN</MenuItem>
              <MenuItem value="LOGIN_FAILED">LOGIN_FAILED</MenuItem>
              <MenuItem value="RUNBOOK_START">RUNBOOK_START</MenuItem>
              <MenuItem value="RUNBOOK_FINISH">RUNBOOK_FINISH</MenuItem>
              <MenuItem value="RUNBOOK_OPEN">RUNBOOK_OPEN</MenuItem>
            </TextField>

            <TextField
              label="Resource Type"
              value={resourceType}
              onChange={(e) => {
                setPage(1);
                const next = e.target.value;
                setResourceType(next);
                // Avoid confusing stale Resource ID when switching types.
                setResourceId('');
              }}
              select
              size="small"
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="camera">camera</MenuItem>
              <MenuItem value="camera_credential">camera_credential</MenuItem>
              <MenuItem value="policy">policy</MenuItem>
              <MenuItem value="deployment">deployment</MenuItem>
              <MenuItem value="incident">incident</MenuItem>
              <MenuItem value="configuration">configuration</MenuItem>
              <MenuItem value="device">device</MenuItem>
              <MenuItem value="pipeline_control">pipeline_control</MenuItem>
              <MenuItem value="ops_runbook">ops_runbook</MenuItem>
            </TextField>

            {resourceType === 'camera' || resourceType === 'policy' ? (
              <Autocomplete
                options={resourceOptions}
                value={resourceOptions.find((o) => o.id === resourceIdQuery) || null}
                onChange={(_, v) => {
                  setPage(1);
                  // Keep UI friendly label, but query will normalize back to id.
                  setResourceId(v?.label || '');
                }}
                inputValue={resourceId}
                onInputChange={(_, v) => {
                  setPage(1);
                  setResourceId(v || '');
                }}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} label="Resource ID" placeholder="pick or paste…" size="small" sx={{ minWidth: 280 }} />
                )}
                clearOnEscape
                autoHighlight
              />
            ) : (
              <TextField
                label="Resource ID"
                placeholder="exact match…"
                value={resourceId}
                onChange={(e) => {
                  setPage(1);
                  setResourceId(e.target.value);
                }}
                size="small"
                sx={{ minWidth: 220 }}
              />
            )}

            <TextField
              label="Actor"
              placeholder="username contains…"
              value={username}
              onChange={(e) => {
                setPage(1);
                setUsername(e.target.value);
              }}
              size="small"
              sx={{ minWidth: 220, flex: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={onlyFailures}
                  onChange={(e) => {
                    setPage(1);
                    setOnlyFailures(e.target.checked);
                  }}
                />
              }
              label="Only failures"
            />

            <Button
              variant="outlined"
              onClick={() => {
                // Clear all filters and the URL query params
                navigate('/audit', { replace: true });
              }}
            >
              Clear
            </Button>
          </Stack>
          {error && (
            <Typography sx={{ mt: 1 }} color="error">
              {error}
            </Typography>
          )}
          {loading && (
            <Typography sx={{ mt: 1 }} color="text.secondary">
              Loading…
            </Typography>
          )}
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Resource</TableCell>
              <TableCell>Success</TableCell>
              <TableCell>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((r) => (
              <TableRow key={r.displayId}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTs(r.timestamp)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.username || r.user_id || '-'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Chip size="small" label={r.displayAction || r.action || '-'} variant="outlined" />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2">
                    <strong>{r.resource_type || '-'}</strong>
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ cursor: r.resource_type && r.resource_id ? 'pointer' : 'default' }}
                    title={r.resource_type && r.resource_id ? 'Click to filter by this resource' : undefined}
                    onClick={() => {
                      if (r.resource_type && r.resource_id) {
                        openFiltered({ resource_type: r.resource_type, resource_id: r.resource_id });
                      }
                    }}
                  >
                    {r.resource_id || ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.success ? 'OK' : 'FAIL'}
                    color={r.success ? 'success' : 'error'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 520 }}>
                  {r.groupedReviewSession ? (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {`${r.groupedReviewSession.events.length} step update${r.groupedReviewSession.events.length === 1 ? '' : 's'} in one review session`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {summarizeAuditDetail(r)}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {summarizeAuditDetail(r)}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!displayRows.length && !loading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary">No audit events found.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total: {total}
        </Typography>
        <Pagination
          count={pages}
          page={page}
          onChange={(_, p) => setPage(p)}
          color="primary"
          size="small"
        />
      </Stack>
    </Box>
  );
}

